import React from "react";
import { roundLabel } from "./totwUtils";

// =====================================================================
// TOTW — desktop filter cascade (frames 1c/1l): labeled dropdown boxes
// Country -> Competition -> Season, then round arrows around a Round
// dropdown with a 6-column round grid. Only levels that exist in the
// data render (the BE cascade already dropped empty levels).
// =====================================================================

const DropdownBox = ({ label, value, open, onToggle, minWidth, children }) => (
  <div className="relative">
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      className="flex flex-col gap-0.5 bg-white border border-[#E2E8F0] hover:border-[#C7D3E3] rounded-[10px] cursor-pointer"
      style={{ padding: "7px 12px", minWidth }}
    >
      <span className="font-bold uppercase text-[#94A3B8]" style={{ fontSize: 10, letterSpacing: "0.06em" }}>
        {label}
      </span>
      <span className="flex items-center justify-between gap-2.5 font-bold text-[#0F172A]" style={{ fontSize: 13.5 }}>
        <span data-no-translate="true" className="whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: 180 }}>
          {value}
        </span>
        <span className="text-[#94A3B8]" style={{ fontSize: 10 }}>
          ▾
        </span>
      </span>
    </div>
    {open && children}
  </div>
);

const OptionList = ({ options, selectedId, onPick, width = 216 }) => (
  <div
    className="absolute z-40 bg-white border border-[#E2E8F0] rounded-xl overflow-auto"
    style={{ top: "calc(100% + 8px)", left: 0, width, maxHeight: 284, boxShadow: "0 18px 40px rgba(15,23,42,0.14)", padding: 6 }}
    onClick={(e) => e.stopPropagation()}
  >
    {options.map((option) => (
      <div
        key={option.id ?? "null"}
        role="button"
        tabIndex={0}
        onClick={() => onPick(option)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPick(option);
          }
        }}
        className="flex items-center justify-between gap-2 rounded-lg hover:bg-[#F1F5F9] cursor-pointer font-semibold text-[#0F172A]"
        style={{ padding: "8px 10px", fontSize: 13 }}
      >
        <span data-no-translate={option.noTranslate ? "true" : undefined} className="whitespace-nowrap overflow-hidden text-ellipsis">
          {option.label}
        </span>
        <span className="text-[#2283FF] font-extrabold" style={{ fontSize: 12 }}>
          {option.id === selectedId ? "✓" : ""}
        </span>
      </div>
    ))}
  </div>
);

const ArrowButton = ({ direction, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-label={direction === "prev" ? "Previous round" : "Next round"}
    className={`flex items-center justify-center bg-white border border-[#E2E8F0] rounded-[10px] font-extrabold ${
      disabled ? "text-[#CBD5E1] cursor-default" : "text-[#475569] hover:border-[#2283FF] hover:text-[#2283FF] cursor-pointer"
    }`}
    style={{ width: 42, fontSize: 15 }}
  >
    {direction === "prev" ? "‹" : "›"}
  </button>
);

const TOTWFiltersBar = ({
  countries = [],
  selection,
  rounds = [],
  openMenu,
  onToggleMenu,
  onPickCountry,
  onPickCompetition,
  onPickSeason,
  onPickRound,
  onStepRound,
}) => {
  const country = countries.find((c) => c.id === selection.countryId) || null;
  const competitions = country?.competitions || [];
  const competition = competitions.find((c) => c.id === selection.competitionId) || null;
  const seasons = competition?.seasons || [];
  const season = seasons.find((s) => s.id === selection.seasonId) || null;
  const roundIndex = rounds.findIndex((r) => r.uuid === selection.roundUuid);
  const currentRound = roundIndex >= 0 ? rounds[roundIndex] : null;
  const latestUuid = rounds.length ? rounds[rounds.length - 1].uuid : null;
  const showingLatest = currentRound && currentRound.uuid === latestUuid;

  return (
    <div className="flex items-end gap-2.5 flex-wrap">
      <DropdownBox
        label="Country"
        value={country?.name || "International"}
        open={openMenu === "country"}
        onToggle={() => onToggleMenu("country")}
        minWidth={132}
      >
        <OptionList
          options={countries.map((c) => ({ id: c.id, label: c.name || "International", noTranslate: Boolean(c.name), raw: c }))}
          selectedId={selection.countryId}
          onPick={(option) => onPickCountry(option.raw)}
        />
      </DropdownBox>

      <DropdownBox
        label="Competition"
        value={competition?.name || ""}
        open={openMenu === "competition"}
        onToggle={() => onToggleMenu("competition")}
        minWidth={146}
      >
        <OptionList
          options={competitions.map((c) => ({ id: c.id, label: c.name || "", noTranslate: true, raw: c }))}
          selectedId={selection.competitionId}
          onPick={(option) => onPickCompetition(option.raw)}
        />
      </DropdownBox>

      <DropdownBox
        label="Season"
        value={season?.year || season?.id || ""}
        open={openMenu === "season"}
        onToggle={() => onToggleMenu("season")}
        minWidth={118}
      >
        <OptionList
          options={seasons.map((s) => ({ id: s.id, label: s.year || s.id, noTranslate: true, raw: s }))}
          selectedId={selection.seasonId}
          onPick={(option) => onPickSeason(option.raw)}
          width={190}
        />
      </DropdownBox>

      <div className="flex items-stretch gap-1.5">
        <ArrowButton direction="prev" disabled={roundIndex <= 0} onClick={() => onStepRound(-1)} />
        <div className="relative">
          <DropdownBox
            label="Round"
            value={currentRound ? roundLabel(currentRound) : ""}
            open={openMenu === "round"}
            onToggle={() => onToggleMenu("round")}
            minWidth={118}
          >
            <div
              className="absolute z-40 bg-white border border-[#E2E8F0] rounded-xl"
              style={{ top: "calc(100% + 8px)", right: 0, width: 268, boxShadow: "0 18px 40px rgba(15,23,42,0.14)", padding: 10 }}
              onClick={(e) => e.stopPropagation()}
            >
              {showingLatest && (
                <div className="font-semibold text-[#64748B] bg-[#F8FAFC] rounded-lg" style={{ fontSize: 11.5, padding: "7px 9px", marginBottom: 8, lineHeight: 1.35 }}>
                  Showing the latest available round.
                </div>
              )}
              <div className="grid gap-[5px]" style={{ gridTemplateColumns: "repeat(6, 1fr)" }}>
                {rounds.map((round) => {
                  const selected = round.uuid === selection.roundUuid;
                  return (
                    <div
                      key={round.uuid}
                      role="button"
                      tabIndex={0}
                      onClick={() => onPickRound(round)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onPickRound(round);
                        }
                      }}
                      data-no-translate="true"
                      className={`flex items-center justify-center rounded-lg font-bold cursor-pointer hover:outline hover:outline-1 hover:outline-[#C7D3E3] ${
                        selected ? "bg-[#2283FF] text-white" : "bg-[#F5F7FA] text-[#334155]"
                      }`}
                      style={{ height: 34, fontSize: 12.5 }}
                    >
                      {round.name || round.round || ""}
                    </div>
                  );
                })}
              </div>
            </div>
          </DropdownBox>
        </div>
        <ArrowButton
          direction="next"
          disabled={roundIndex < 0 || roundIndex >= rounds.length - 1}
          onClick={() => onStepRound(1)}
        />
      </div>
    </div>
  );
};

export default TOTWFiltersBar;
